// src/pages/Dashboard.tsx
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import '../styles/Dashboard.css';
import { API_BASE_URL } from '../config'; // Add this import

type Tab = 'home' | 'today' | 'progress' | 'settings';

interface Workout {
  id: number;
  name: string;
  start_time: string;
  notes: string;
  total_duration_minutes?: number;
  calories_burned?: number;
}

interface DashboardProps {
  onLogWorkout: () => void;
  refreshKey: number;
}

const Dashboard: React.FC<DashboardProps> = ({ onLogWorkout, refreshKey }) => {
  const token = localStorage.getItem('flab2fabs_token');
  const userRaw = localStorage.getItem('flab2fabs_user');

  const [user, setUser] = useState<{ name: string } | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('home');
  const [loading, setLoading] = useState(true);

  const [workouts, setWorkouts] = useState<Workout[]>([]);
  const [strength, setStrength] = useState<any>(null);
  const [consistency, setConsistency] = useState<any>(null);
  const [workoutCount, setWorkoutCount] = useState(0);
  const [streak, setStreak] = useState(5);
  const [intScore, setIntScore] = useState(78);
  const [level, setLevel] = useState(12);

  /* AUTH */
  useEffect(() => {
    if (!token || !userRaw) return;
    setUser(JSON.parse(userRaw));
  }, [token, userRaw]);

  /* LOAD DATA */
  const loadDashboard = async () => {
    if (!token) return;
    const headers = { 
      Authorization: `Bearer ${token}` 
    };

    try {
      const [w, s, c] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/workouts/`, { headers }),
        axios.get(`${API_BASE_URL}/api/progress/strength-projections`, { headers }),
        axios.get(`${API_BASE_URL}/api/progress/consistency-projections`, { headers }),
      ]);

      setWorkouts(w.data);
      setWorkoutCount(w.data.length);
      setStrength(s.data);
      setConsistency(c.data);
      
      // Update counters based on actual data
      if (s.data?.projections?.estimated_strength_gain_kg) {
        // You could update streak based on recent workouts
        const recentWorkouts = w.data.filter((workout: Workout) => {
          const workoutDate = new Date(workout.start_time);
          const today = new Date();
          const diffTime = Math.abs(today.getTime() - workoutDate.getTime());
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          return diffDays <= 7; // Last 7 days
        });
        
        // Simple streak calculation - adjust as needed
        const newStreak = Math.min(recentWorkouts.length, 14); // Cap at 14
        setStreak(newStreak);
      }
      
    } catch (e) {
      console.error('Dashboard load failed', e);
      if (axios.isAxiosError(e) && e.response?.status === 401) {
        // Token expired, logout
        localStorage.clear();
        window.location.href = '/login';
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, [refreshKey]);

  if (loading || !user) {
    return (
      <div className="loading-container">
        <div className="progress-card loading-card">
          <div className="loading-spinner"></div>
          <p>Loading your fitness dashboard...</p>
        </div>
      </div>
    );
  }

  const todayWorkout = workouts.at(-1);
  const todayDate = new Date().toLocaleDateString('en-US', { 
    weekday: 'long', 
    month: 'long', 
    day: 'numeric' 
  });

  /* ---------------- TAB CONTENT ---------------- */

  const renderHome = () => (
    <>
      {/* Welcome Card */}
      <div className="progress-card welcome-card">
        <h3 className="welcome-title">
          👋 Welcome back, <span className="username-highlight">{user.name}</span>!
        </h3>
        <p className="welcome-subtitle">
          {todayDate} • Your emotional training partner for fitness success
        </p>
      </div>

      {/* Quick Stats */}
      <div className="stats-grid">
        <div className="stat-item">
          <div className="stat-value">{workoutCount}</div>
          <div className="stat-label">Workouts</div>
        </div>
        <div className="stat-item">
          <div className="stat-value streak-value">{streak}</div>
          <div className="stat-label">Day Streak</div>
        </div>
        <div className="stat-item">
          <div className="stat-value int-score">{intScore}%</div>
          <div className="stat-label">Int Score</div>
        </div>
        <div className="stat-item">
          <div className="stat-value level">Lvl {level}</div>
          <div className="stat-label">Level</div>
        </div>
      </div>

      {/* Today's Activity */}
      <section className="progress-section">
        <h3 className="section-title">🔥 Today's Activity</h3>
        
        {todayWorkout ? (
          <div className="today-activity-card">
            <strong className="activity-title">{todayWorkout.name}</strong>
            <p className="activity-time">
              🕒 {new Date(todayWorkout.start_time).toLocaleTimeString([], { 
                hour: '2-digit', 
                minute: '2-digit' 
              })}
            </p>
            {todayWorkout.notes && <p className="activity-notes">📝 {todayWorkout.notes}</p>}
            
            <div className="workout-stats">
              {todayWorkout.total_duration_minutes && (
                <span className="workout-stat">
                  ⏱️ {todayWorkout.total_duration_minutes}min
                </span>
              )}
              {todayWorkout.calories_burned && (
                <span className="workout-stat">
                  🔥 {todayWorkout.calories_burned}cal
                </span>
              )}
            </div>
            
            <span className="badge-success">Completed</span>
          </div>
        ) : (
          <div className="progress-card activity-empty-card">
            <p className="empty-message">No workout logged today. Start your fitness journey!</p>
            <button className="primary-btn log-btn" onClick={onLogWorkout}>
              ➕ Log Today's Workout
            </button>
          </div>
        )}
      </section>

      {/* Progress Projections */}
      <section className="progress-section">
        <h3 className="section-title">📈 Progress Projections</h3>
        <div className="progress-card projections-card">
          <div className="progress-container">
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: '75%' }}  // This would be dynamic
              />
            </div>
          </div>
          
          <div className="projections-stats">
            <div className="projection-stat">
              <div className="projection-value gain-value">
                +{strength?.projections?.estimated_strength_gain_kg || '12.5'}kg
              </div>
              <div className="projection-label">Potential Gain</div>
            </div>
            <div className="projection-stat">
              <div className="projection-value missed-value">
                {strength?.projections?.emotional_impact?.total_missed_kg || '8.2'}kg
              </div>
              <div className="projection-label">Missed</div>
            </div>
          </div>
          
          <p className="motivation-message">
            💬 {strength?.projections?.emotional_impact?.motivation_messages?.[0] || 
                "You could have lifted more! Stay focused."}
          </p>
        </div>
      </section>

      {/* Quick Actions */}
      <div className="progress-card quick-actions-card">
        <h3 className="section-title">Quick Actions</h3>
        <div className="action-buttons">
          <button className="primary-btn action-btn log-today-btn" onClick={onLogWorkout}>
            💪 Log Today
          </button>
          <button className="secondary-btn action-btn view-report-btn">
            📊 View Report
          </button>
        </div>
      </div>
    </>
  );

  const renderToday = () => (
    <section className="progress-section">
      <h3 className="section-title">🗓️ Today's Recommendation</h3>
      <div className="progress-card recommendation-card">
        <h4 className="recommendation-title">
          Pull-Ups
        </h4>
        <p><strong>Focus:</strong> Back</p>
        <p><strong>Status:</strong> <span className="status-ready">Fully rested and ready</span></p>
        
        <div className="progress-container">
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: '30%' }} />
          </div>
        </div>
        
        <p className="insight-text">
          <strong>💡 Insight:</strong> Your back is undertrained. Focus on proper form.
        </p>
        
        <button className="primary-btn start-workout-btn" onClick={onLogWorkout}>
          🏋️ Start This Workout
        </button>
      </div>
    </section>
  );

  const renderProgress = () => (
    <>
      <section className="progress-section">
        <h3 className="section-title">📊 Consistency & Emotional Score</h3>

        <div className="progress-card consistency-card">
          <div className="progress-container">
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ 
                  width: `${consistency?.projections?.current_consistency_score || 75}%` 
                }} 
              />
            </div>
          </div>
          
          <div className="consistency-stats">
            <div className="consistency-stat">
              <div className="consistency-value">
                {consistency?.projections?.current_consistency_score || 75}%
              </div>
              <div className="consistency-label">Current</div>
            </div>
            <div className="consistency-stat">
              <div className="consistency-value">
                {consistency?.projections?.projected_90_day_score || 90}%
              </div>
              <div className="consistency-label">90-Day Goal</div>
            </div>
            <div className="consistency-stat">
              <div className="consistency-value">
                {consistency?.projections?.missed_workouts || 3}
              </div>
              <div className="consistency-label">Missed</div>
            </div>
            <div className="consistency-stat">
              <div className="consistency-value streak-value">{streak}</div>
              <div className="consistency-label">Day Streak</div>
            </div>
          </div>
          
          <p className="consistency-insight">
            <strong>🎯 Insight:</strong> {consistency?.projections?.current_consistency_score >= 80 
              ? "You're doing amazing! Keep up the consistency."
              : "Stay consistent to reach your full potential."}
          </p>
        </div>
      </section>

      <section className="progress-section">
        <h3 className="section-title">📈 Strength Projections</h3>
        <div className="progress-card strength-card">
          <div className="strength-stats">
            <div className="strength-stat">
              <div className="strength-value gain-value">
                +{strength?.projections?.estimated_strength_gain_kg || '12.5'}kg
              </div>
              <div className="strength-label">Gain</div>
            </div>
            <div className="strength-stat">
              <div className="strength-value missed-value">
                {strength?.projections?.emotional_impact?.total_missed_kg || '8.2'}kg
              </div>
              <div className="strength-label">Missed</div>
            </div>
          </div>
          
          <div className="progress-container">
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ 
                  width: `${Math.min(
                    ((strength?.projections?.estimated_strength_gain_kg || 12.5) / 20) * 100, 
                    100
                  )}%` 
                }} 
              />
            </div>
          </div>
          
          <p className="strength-motivation">
            {strength?.projections?.emotional_impact?.motivation_messages?.[0] || 
             "Every workout counts. Keep pushing forward!"}
          </p>
        </div>
      </section>
    </>
  );

  const renderSettings = () => (
    <section className="progress-section">
      <h3 className="section-title">⚙️ Settings</h3>
      <div className="progress-card settings-card">
        <p className="user-info">Logged in as <strong>{user.name}</strong></p>
        
        <div className="settings-actions">
          <button className="settings-btn profile-btn">
            👤 Edit Profile
          </button>
          <button className="settings-btn notifications-btn">
            🔔 Notification Settings
          </button>
          <button className="settings-btn darkmode-btn">
            🌙 Dark Mode
          </button>
          <button 
            className="primary-btn logout-btn"
            onClick={() => {
              localStorage.clear();
              window.location.href = '/login';
            }}
          >
            🚪 Log out
          </button>
        </div>
      </div>
    </section>
  );

  return (
    <div className="dashboard">
      {/* HEADER - Only on home tab */}
      {activeTab === 'home' && (
        <header className="dashboard-header">
          <h1>🔥 Flab2Fabs</h1>
          <p>Emotional training partner for fitness success</p>
        </header>
      )}

      {/* TAB HEADER */}
      {activeTab !== 'home' && (
        <div className="progress-card tab-header-card">
          <h2 className="tab-title">
            {activeTab === 'today' && '💪 Today'}
            {activeTab === 'progress' && '📊 Progress'}
            {activeTab === 'settings' && '⚙️ Settings'}
          </h2>
        </div>
      )}

      {/* CONTENT */}
      {activeTab === 'home' && renderHome()}
      {activeTab === 'today' && renderToday()}
      {activeTab === 'progress' && renderProgress()}
      {activeTab === 'settings' && renderSettings()}

      {/* BOTTOM NAV */}
      <nav className="bottom-nav">
        <button
          className={`nav-item ${activeTab === 'home' ? 'active' : ''}`}
          onClick={() => setActiveTab('home')}
        >
          <span className="nav-icon">🏠</span>
          <span className="nav-label">Home</span>
        </button>
        <button
          className={`nav-item ${activeTab === 'progress' ? 'active' : ''}`}
          onClick={() => setActiveTab('progress')}
        >
          <span className="nav-icon">📊</span>
          <span className="nav-label">Progress</span>
        </button>
        <button
          className={`nav-item ${activeTab === 'today' ? 'active' : ''}`}
          onClick={() => setActiveTab('today')}
        >
          <span className="nav-icon">💪</span>
          <span className="nav-label">Today</span>
        </button>
        <button
          className={`nav-item ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          <span className="nav-icon">⚙️</span>
          <span className="nav-label">Settings</span>
        </button>
      </nav>
    </div>
  );
};

export default Dashboard;
